import { startsWith, find } from 'lodash';
import NavResource from '@/modules/core/nav/resources/NavResource';
import { RouteGroup } from '@/modules/core/app/constants/api.constants';
import { UPDATE_NAV_ITEM } from '@/modules/core/nav/nav.constants';
import EventBus from '@/modules/core/app/helpers/EventBus';
import OnboardingService from '@/modules/core/onboarding/services/OnboardingService';
import { HintReportsRoute } from '@/modules/ta/hintreports/hintreports.constants';

class NavService {
  /**
   * @returns {Promise<array>}
   */
  async getAppNavItems() {
    return NavResource.getAppNavItems();
  }

  /**
   * @param {AppNavItem[]} navs
   * @param routePath
   * @returns {*}
   */
  resolveActiveAppNavItem(navs, routePath) {
    const activeNav = find(navs, (nav) => this.resolveActiveNavItem(nav.items, routePath));
    return activeNav && activeNav.key;
  }

  /**
   *
   * @param items
   * @param routePath
   */
  resolveActiveNavItem(items, routePath) {
    return find(items, (item) => this.isActiveNavItem(item, routePath));
  }

  /**
   *
   * @param item
   * @param routePath
   * @returns {boolean}
   */
  isActiveNavItem(item, routePath) {
    if (!item.link) {
      return false;
    }

    if (routePath.startsWith('/fav/')) {
      return `/${item.link}` === routePath;
    }

    const isResearchRoute = routePath.startsWith(RouteGroup.RESEARCH);

    if (isResearchRoute) {
      return startsWith(routePath, `/${item.link}`);
    }

    const isDrilldownRoute =
      routePath.includes(RouteGroup.SERVICES_DRILLDOWN) ||
      routePath.includes(RouteGroup.CATEGORIES_DRILLDOWN);

    const isDashRoute = routePath.includes(RouteGroup.DASH);
    const isLeadsDashRoute = routePath === `/${RouteGroup.LEADS}`;
    const isTemplateRoute = routePath.includes('/templates');

    const isManageServiceRoute =
      routePath.startsWith(`/${RouteGroup.SERVICE_MANAGE}`) ||
      routePath.startsWith(`/${RouteGroup.SERVICE_MAPPINGS}`);

    if (routePath === `/${RouteGroup.CONNECT_DATA}`) {
      return item.link === RouteGroup.CONNECT_DATA;
    }

    if (routePath === `/${RouteGroup.CREATE_DASHBOARD}`) {
      return item.link === RouteGroup.CREATE_DASHBOARD;
    }

    if (routePath === `/${RouteGroup.CREATE_SMARTSLIDE}`) {
      return item.link === RouteGroup.CREATE_SMARTSLIDE;
    }

    if (routePath.startsWith(`/${HintReportsRoute.HINT_REPORTS}`)) {
      return item.link === HintReportsRoute.HINT_REPORTS_SLIDES;
    }

    if (isTemplateRoute) {
      const templatesPath = routePath.slice(
        0,
        routePath.indexOf('/templates') + '/templates'.length
      );
      return `/${item.link}` === templatesPath;
    }
    if (isDrilldownRoute) {
      return item.link === RouteGroup.DRILLDOWNS;
    }
    if (routePath.startsWith(`/${RouteGroup.SALES_DASHBOARD}`)) {
      return item.link === RouteGroup.SALES_DASHBOARD;
    }

    if (isDashRoute && !isLeadsDashRoute) {
      return item.link === RouteGroup.DASHBOARDS;
    }
    if (isManageServiceRoute) {
      return item.link === RouteGroup.SERVICE_MANAGE;
    }

    return routePath === `/${item.link}` || startsWith(routePath, `/${item.link}/`);
  }

  /**
   * @param item
   * @returns {boolean}
   */
  isNavItemDisabled(item) {
    return !!OnboardingService.isOnboardingNavItemDisabled(item) || item.disabled;
  }

  /**
   * @param item
   * @returns {string}
   */
  getNavItemIcon(item) {
    let { icon } = item;
    if (item.isOnboradingItem()) {
      icon = OnboardingService.getOnboardingStepIcon(item);
    }

    return `ti-${icon}`;
  }

  /**
   * @param item
   * @returns {string|null}
   */
  getIconClass(item) {
    if (OnboardingService.hasOnboardingStepCompleted(item)) {
      return 'is-primary';
    }

    if (item.disabled) {
      return 'disabled-nav-item-icon';
    }

    return null;
  }

  /**
   * update the nav items.
   */
  updateNavItems() {
    EventBus.signal(UPDATE_NAV_ITEM);
  }

  /**
   * @returns {Promise<*>}
   */
  async sendInviteEmail(source) {
    return NavResource.sendInviteEmail(source);
  }
}

export default new NavService();
